import { useEffect } from "react";
import { X, Download, Printer, ExternalLink } from "lucide-react";

/**
 * Inline PDF preview modal.
 *
 * Props:
 *   open     bool
 *   url      object URL (blob:) or remote URL of the PDF
 *   title    string for the header
 *   onClose  () => void
 *   onDownload (optional) action that triggers a real download (so the parent
 *             can fetch a fresh blob with the auth header)
 */
export default function PDFModal({ open, url, title, onClose, onDownload }) {
  // ESC closes the modal
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open || !url) return null;

  const print = () => {
    const w = window.open(url, "_blank");
    if (w) {
      w.addEventListener("load", () => {
        try {
          w.focus();
          w.print();
        } catch {
          /* print blocked by browser; user can trigger from PDF viewer */
        }
      });
    }
  };

  return (
    <div
      data-testid="pdf-modal"
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-5xl h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "var(--green-50)", color: "var(--green-700)" }}
            >
              <ExternalLink size={14} />
            </div>
            <div className="truncate">
              <div className="text-sm font-bold truncate" data-testid="pdf-modal-title">
                {title || "Document preview"}
              </div>
              <div className="text-xs muted">Inline preview · zoom / scroll inside the viewer</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="pdf-modal-print"
              onClick={print}
              className="btn btn-ghost"
              title="Print"
            >
              <Printer size={14} /> Print
            </button>
            {onDownload && (
              <button
                data-testid="pdf-modal-download"
                onClick={onDownload}
                className="btn btn-ghost"
                title="Download"
              >
                <Download size={14} /> Download
              </button>
            )}
            <button
              data-testid="pdf-modal-close"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 ml-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 bg-slate-50">
          <iframe
            data-testid="pdf-modal-frame"
            src={url}
            title="PDF preview"
            style={{ width: "100%", height: "100%", border: 0 }}
          />
        </div>
      </div>
    </div>
  );
}
